import { IconName } from '@blueprintjs/icons';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, NavLink, useHistory } from 'react-router-dom';
import Box, { BoxProps } from 'ui-box';
import { GlobalHotKeys } from 'react-hotkeys';
import {
  Heading,
  defaultFills,
  Tag,
  Icon,
  Button,
  majorScale,
} from '../../npaper';
import { Card } from '../../npaper/Card';
import { useStores } from '../../stores';
import { CompanyDetailsDialog } from '../companies/CompanyDetailsDialog';
import { SaleDetailDialog } from '../sales/SaleDetailsDialog';

import styles from './Navigation.module.css';
import { OrganisationIcon } from '../settings/OrganisationIcon';
import { LookupDialog } from './LookupDialog';
import moment from 'moment';

const NavigationHead = observer(() => {
  const history = useHistory();

  const { authStore } = useStores();

  return (
    <div className={styles.navigationHead} onClick={() => history.push('/')}>
      {authStore.currentOrganisation && (
        <OrganisationIcon
          organisation={authStore.currentOrganisation}
          marginRight={majorScale(1)}
        />
      )}
      <div>
        <Box is="p" fontWeight={500}>
          {authStore.currentOrganisation && authStore.currentOrganisation.name}
        </Box>
        <Box is="p" className="small muted">
          {authStore.me && authStore.me.name}
        </Box>
      </div>
    </div>
  );
});

export const ShortCutTag = ({
  icon,
  text,
  ...props
}: { icon?: IconName; text: string } & BoxProps<any>) => (
  <Tag
    is="div"
    className={classNames('font-mono', styles.shortCut)}
    padding={4}
    height="20px"
    {...props}
  >
    {icon && (
      <Icon
        icon={icon}
        color={defaultFills.normal[600]}
        size={12}
        style={{ lineHeight: '12px' }}
      />
    )}
    <Box is="div" fontWeight={600} color={defaultFills.normal[600]}>
      {text}
    </Box>
  </Tag>
);

const NavigationLookUp = ({ onClick }: { onClick: () => void }) => (
  <Card
    className={styles.navigationLookUp}
    elevation={1}
    marginTop={16}
    marginX={-6}
    onClick={() => onClick()}
  >
    <Icon icon="search" color={defaultFills.normal[400]} />
    <p>Quick look</p>
    <ShortCutTag icon="key-command" text="K" />
  </Card>
);

export const Navigation: React.FC = observer(() => {
  const history = useHistory();
  const { authStore } = useStores();

  const [lookup, setLookup] = useState(false);
  const [createOffer, setCreateOffer] = useState(false);
  const [createCompany, setCreateCompany] = useState(false);

  const keyMap = {
    createSale: 'ctrl+n',
    createCompany: 'ctrl+m',
    showLookup: 'cmd+k',
  };

  const handlers = {
    createSale: (event?: KeyboardEvent) => {
      event && event.preventDefault();
      setCreateOffer(true);
    },
    createCompany: (event?: KeyboardEvent) => {
      event && event.preventDefault();
      setCreateCompany(true);
    },
    showLookup: (event?: KeyboardEvent) => {
      event && event.preventDefault();
      setLookup(true);
    },
  };

  const subscription = authStore.currentOrganisation!.subscription;

  return (
    <GlobalHotKeys keyMap={keyMap} handlers={handlers}>
      <Box className={styles.navigation} zIndex={99} position="absolute">
        <LookupDialog isShown={lookup} onClose={() => setLookup(false)} />
        <SaleDetailDialog
          isShown={createOffer}
          onClose={(sale) => {
            setCreateOffer(false);
            if (sale) {
              history.push(`/sales/${sale.id}`);
            }
          }}
        />
        <CompanyDetailsDialog
          isShown={createCompany}
          onClose={(company) => {
            setCreateCompany(false);
            if (company) {
              history.push(`/companies/${company.id}`);
            }
          }}
        />

        {(!subscription || subscription.trialing) && (
          <Button
            is={Link}
            to="/settings/subscription?upgrade=true"
            intent="info"
            primary
            className="text-center mb-4 -mt-2"
            height={44}
            display="flex"
          >
            <div>
              <FormattedMessage defaultMessage="Ready to upgrade?" />
            </div>
            <div className="text-xs font-medium text-blue-200">
              <FormattedMessage
                defaultMessage="{days, plural, =1 {# day} other {# days}} left on trial"
                values={{
                  days: !subscription
                    ? 0
                    : moment(subscription.trial_end).diff(moment(), 'days') + 1, // Add a day for slack
                }}
              />
            </div>
          </Button>
        )}
        {subscription &&
          subscription.active &&
          !subscription.trialing &&
          subscription.cancel_at_period_end && (
            <Button
              is={Link}
              to="/settings/subscription?upgrade=true"
              intent="info"
              primary
              className="text-center mb-4 -mt-2"
              height={44}
            >
              <div>
                <FormattedMessage defaultMessage="Reactivate subscription" />
              </div>
              <div className="text-xs font-medium text-blue-200">
                <FormattedMessage
                  defaultMessage="{days, plural, =1 {# day} other {# days}} left"
                  values={{
                    days:
                      moment(subscription.current_period_end).diff(
                        moment(),
                        'days'
                      ) + 1, // Add a day for slack
                  }}
                />
              </div>
            </Button>
          )}
        {subscription && !subscription.active && (
          <Button
            is={Link}
            to="/settings/subscription?upgrade=true"
            intent="info"
            primary
            className="text-center mb-4 -mt-2"
            height={44}
          >
            <div>
              <FormattedMessage defaultMessage="Reactivate subscription" />
            </div>
            <div className="text-xs font-medium text-blue-200">
              <FormattedMessage defaultMessage="Get access again" />
            </div>
          </Button>
        )}

        <NavigationHead />

        <NavigationLookUp onClick={() => setLookup(true)} />

        <Heading size={100} marginTop={32} marginBottom={8}>
          <FormattedMessage defaultMessage="Contacts" />
        </Heading>
        <NavLink
          to="/companies"
          className={styles.navigationItem}
          activeClassName={styles.active}
        >
          <div>🏢</div>
          <p>
            <FormattedMessage defaultMessage="Companies" />
          </p>
        </NavLink>
        <NavLink
          to="/contacts"
          className={styles.navigationItem}
          activeClassName={styles.active}
        >
          <div>👥</div>
          <p>
            <FormattedMessage defaultMessage="Contacts" />
          </p>
        </NavLink>
        <NavLink
          to="/contact-groups"
          className={styles.navigationItem}
          activeClassName={styles.active}
        >
          <div>🎯</div>
          <p>
            <FormattedMessage defaultMessage="Customer groups" />
          </p>
        </NavLink>

        <Heading size={100} marginTop={32} marginBottom={8}>
          <FormattedMessage defaultMessage="Sales" />
        </Heading>
        <NavLink
          to="/offers"
          className={styles.navigationItem}
          activeClassName={styles.active}
        >
          <div>👋</div>
          <p>
            <FormattedMessage defaultMessage="Deals" />
          </p>
        </NavLink>
        <NavLink
          to="/orders"
          className={styles.navigationItem}
          activeClassName={styles.active}
        >
          <div>🤝</div>
          <p>
            <FormattedMessage defaultMessage="Orders" />
          </p>
        </NavLink>
        <NavLink
          to="/invoices"
          className={styles.navigationItem}
          activeClassName={styles.active}
        >
          <div>💸</div>
          <p>
            <FormattedMessage defaultMessage="Invoices" />
          </p>
        </NavLink>
        {/* TODO: <NavLink
          to="/agreements"
          className={styles.navigationItem}
          activeClassName={styles.active}
        >
          <div>✍️</div>
          <p>
            <FormattedMessage defaultMessage="Ongoing agreements" />
          </p>
        </NavLink>*/}

        <Heading size={100} marginTop={32} marginBottom={8}>
          <FormattedMessage defaultMessage="Service" />
        </Heading>
        {/* TODO: Implement projects page
        <NavLink
          to="/projects"
          className={styles.navigationItem}
          activeClassName={styles.active}
        >
          <div>📅</div>
          <p>
            <FormattedMessage defaultMessage="Projects" />
          </p>
        </NavLink>*/}
        <NavLink
          to="/tasks"
          className={styles.navigationItem}
          activeClassName={styles.active}
        >
          <div>⏱️</div>
          <p>
            <FormattedMessage defaultMessage="Tasks" />
          </p>
        </NavLink>

        <Heading size={100} marginTop={32} marginBottom={8}>
          <FormattedMessage defaultMessage="Purchasing" />
        </Heading>
        <NavLink
          to="/products"
          className={styles.navigationItem}
          activeClassName={styles.active}
        >
          <div>📦</div>
          <p>
            <FormattedMessage defaultMessage="Products" />
          </p>
        </NavLink>
        <NavLink
          to="/product-groups"
          className={styles.navigationItem}
          activeClassName={styles.active}
        >
          <div>🎛️</div>
          <p>
            <FormattedMessage defaultMessage="Product groups" />
          </p>
        </NavLink>
        <NavLink
          to="/purchase-orders"
          className={styles.navigationItem}
          activeClassName={styles.active}
        >
          <div>🚚</div>
          <p>
            <FormattedMessage defaultMessage="Purchase orders" />
          </p>
        </NavLink>

        <Heading size={100} marginTop={32} marginBottom={8}>
          <FormattedMessage defaultMessage="Settings" />
        </Heading>
        <NavLink
          to="/settings"
          className={styles.navigationItem}
          activeClassName={styles.active}
        >
          <div>⚙️</div>
          <p>
            <FormattedMessage defaultMessage="Settings" />
          </p>
        </NavLink>

        <Heading size={100} marginTop={32} marginBottom={8}>
          <FormattedMessage defaultMessage="Short cuts" />
        </Heading>
        <div
          className={styles.navigationItem}
          onClick={() => setCreateOffer(true)}
        >
          <div>
            <Icon icon="plus" color={defaultFills.normal[500]} />
          </div>
          <p>
            <FormattedMessage defaultMessage="Create deal" />
          </p>
          <ShortCutTag icon="key-control" text="N" marginLeft="auto" />
        </div>
        <div
          className={styles.navigationItem}
          onClick={() => setCreateCompany(true)}
        >
          <div>
            <Icon icon="plus" color={defaultFills.normal[500]} />
          </div>
          <p>
            <FormattedMessage defaultMessage="Create company" />
          </p>
          <ShortCutTag icon="key-control" text="M" marginLeft="auto" />
        </div>

        <Box justifySelf="flex-end" marginTop="auto">
          <Button
            iconLeft="log-out"
            minimal
            marginX="auto"
            onClick={() => authStore.signOut()}
            marginTop={majorScale(3)}
          >
            Log af
          </Button>
        </Box>
      </Box>
    </GlobalHotKeys>
  );
});
